<script lang="ts">
  /**
   * The Daily tab: the deal everyone shares, on a clock. The shell's
   * `ChallengesTemplate` with Interlock's one challenge, and its `Leaderboard`
   * beside it (`docs/decisions/0054`). Home's "Today" card is the short way
   * in; this is the daily in full.
   *
   * An intro sentence, the daily's challenge card (its facts, the countdown
   * to the next one, today's leader, and a filled "Play today"), today's
   * board (rank, name and time, the player's own row in the accent), and a
   * footer sentence.
   *
   * The countdown runs to the server's own rollover instant
   * (`DailyInfo.nextRolloverAtMs`) with the card's own clock (`endsAt`), and
   * when it passes the daily is asked for again. A server that sends no
   * instant gets the device's midnight while the two agree on the day, and
   * no countdown at all while they do not (`state/daily-countdown.ts`).
   *
   * A guest can play: the run is the same, it is only never posted, so the
   * card says so and offers the way to sign in beside Play; the board shows
   * for a guest too. With no connection there is nothing to play and the
   * card says that instead; solo play is on Home and needs none.
   */
  import {
    ChallengeCard,
    ChallengesTemplate,
    Icon,
    Leaderboard,
    Note,
    PillButton,
  } from '@artibens/app-shell';
  import type { ContentState, LeaderboardRow } from '@artibens/app-shell';
  import { formatClock } from '@interlock/modes';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import { account } from '../state/account.svelte.js';
  import { dailyClock } from '../state/daily-clock.svelte.js';
  import { formatCountdown, nextDailyCountdown, usableRollover } from '../state/daily-countdown.js';
  import { loadDailyPlayedDate } from '../state/daily-played-io.js';
  import { dailyDayLabel, dailyStanding } from '../state/daily-view.js';
  import { daily, refreshDaily } from '../state/daily.svelte.js';

  const {
    onDaily,
    onSignIn,
  }: {
    /** Starts today's daily. */
    onDaily: () => void;
    onSignIn: () => void;
  } = $props();

  const info = $derived(daily.info);
  const leader = $derived(info?.leaderboard[0] ?? null);
  const finishedHere = $derived(info !== null && loadDailyPlayedDate() === info.date);
  const isGuest = $derived(account.status === 'guest');
  const ownName = $derived(account.player?.username.toLowerCase() ?? null);

  const detail = $derived.by(() => {
    if (daily.status === 'unknown') return "Checking today's board…";
    if (info === null) return 'Needs a connection';
    return `${dailyDayLabel(info.date)} · ${String(info.goalLines)} lines · Normal · ${dailyStanding(info, finishedHere)}`;
  });

  /** The server's instant, when the card can count down to it by itself. */
  const endsAt = $derived(
    info === null ? null : usableRollover(info.nextRolloverAtMs, dailyClock.nowMs),
  );
  /** Otherwise the device's midnight, or nothing. */
  const fallbackCountdown = $derived(
    info === null || endsAt !== null
      ? null
      : nextDailyCountdown(info.date, new Date(dailyClock.nowMs), info.nextRolloverAtMs),
  );

  const boardState = $derived<ContentState>(
    daily.status === 'unknown' ? 'loading' : info === null ? 'offline' : 'ready',
  );
  const boardRows = $derived<LeaderboardRow[]>(
    (info?.leaderboard ?? []).map((entry, index) => ({
      id: entry.username.toLowerCase(),
      rank: index + 1,
      name: entry.username,
      score: formatClock(entry.result),
    })),
  );

  function startDaily(): void {
    // The AudioContext may only start on a user gesture; this tap is it.
    startAudioForGesture();
    onDaily();
  }
</script>

{#snippet calendarIcon()}<Icon name="calendar" size={22} />{/snippet}
{#snippet playIcon()}<Icon name="play" size={18} />{/snippet}

{#snippet dailyActions()}
  {#if info !== null}
    <PillButton
      label={info.played || finishedHere ? 'Practice again' : 'Play today'}
      icon={playIcon}
      disabled={daily.submitting}
      testid="daily-play"
      onclick={startDaily}
    />
    {#if isGuest}
      <PillButton variant="light" label="Sign in" testid="daily-sign-in" onclick={onSignIn} />
    {/if}
  {/if}
{/snippet}

{#snippet cards()}
  <ChallengeCard
    title="Daily"
    {detail}
    icon={calendarIcon}
    iconBg="linear-gradient(150deg, var(--ui-mark-daily-from), var(--ui-mark-daily-to))"
    iconInk="var(--ui-on-deep)"
    {...endsAt !== null
      ? {
          endsAt,
          formatCountdown,
          onend: () => {
            void refreshDaily();
          },
        }
      : fallbackCountdown !== null
        ? { countdown: fallbackCountdown }
        : {}}
    countdownLabel="Next daily in"
    actions={dailyActions}
    testid="daily-panel"
  >
    {#if daily.status === 'unknown'}
      <!-- Nothing to add until the server has answered. -->
    {:else if info === null}
      <p class="daily-line daily-line--quiet">Solo play on Home works without one.</p>
    {:else}
      <p class="daily-line" data-testid="daily-leader">
        {#if leader !== null}
          Today's leader: {leader.username} · {formatClock(leader.result)}
        {:else}
          Nobody has finished yet
        {/if}
      </p>
      {#if info.played && info.yourRank !== null && info.yourResult !== null}
        <p class="daily-line" data-testid="daily-yours">
          You placed #{info.yourRank} · {formatClock(info.yourResult)}
        </p>
      {:else if isGuest}
        <p class="daily-line daily-line--quiet" data-testid="daily-guest">
          You are playing as a guest. Sign in to put your time on the board.
        </p>
      {/if}
      {#if daily.error}
        <Note tone="error">{daily.error}</Note>
      {/if}
    {/if}
  </ChallengeCard>
{/snippet}

{#snippet board()}
  <Leaderboard
    title="Today's board"
    rows={boardRows}
    ownId={ownName}
    state={boardState}
    emptyText="Nobody has finished yet."
    loadingText="Checking today's board…"
    offlineText="Today's board needs a connection."
    youLabel="You"
    testid="daily-board"
    testids={{ row: 'daily-board-row' }}
  />
{/snippet}

<ChallengesTemplate
  intro="The same run for everyone, once a day."
  {cards}
  aside={board}
  footer="A new daily starts when the countdown runs out."
  testid="daily"
/>

<style>
  .daily-line {
    margin: 0;
    font: var(--ui-text-label);
    font-weight: 800;
    color: var(--ui-ink);
  }

  .daily-line--quiet {
    font-weight: 700;
    color: var(--ui-ink-2);
  }
</style>
